﻿import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users,
  Shield,
  ShieldCheck,
  Search,
  UserPlus,
  Mail,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Crown,
  UserMinus,
  LogOut,
  ChevronDown,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X } from 'lucide-react';
import type { User, GroupMember } from '../types';

interface MemberManagementProps {
  store: {
    users: User[];
    currentUser: User | null;
    searchUser: (email: string) => Promise<User | null>;
    inviteMember: (userId: string) => Promise<void>;
    currentGroupId: string | null;
    groupMembers: GroupMember[];
    updateMemberRole: (groupId: string, userId: string, role: 'admin' | 'member') => Promise<void>;
    removeMember: (groupId: string, userId: string) => Promise<void>;
    refreshGroupMembers: (groupId: string) => Promise<void>;
  };
}

export default function MemberManagement({ store }: MemberManagementProps) {
  const { currentUser, searchUser, currentGroupId, groupMembers, updateMemberRole, removeMember, refreshGroupMembers } = store;

  const [searchTerm, setSearchTerm] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<User | null>(null);
  const [searchError, setSearchError] = useState('');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<GroupMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<GroupMember | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [roleTarget, setRoleTarget] = useState<GroupMember | null>(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  useEffect(() => {
    if (currentGroupId) {
      void refreshGroupMembers(currentGroupId);
    }
  }, [currentGroupId]);

  const currentUserMember = groupMembers.find((m) => m.userId === currentUser?.id);
  const isCurrentUserAdmin = currentUserMember?.role === 'admin';

  const filteredMembers = groupMembers.filter(
    (m) =>
      m.nickname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSearch = async () => {
    if (!inviteEmail) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    try {
      const result = await searchUser(inviteEmail);
      if (result) {
        setSearchResult(result);
      } else {
        setSearchError('找不到該電子郵件對應的會員，請確認輸入是否正確。');
      }
    } catch {
      setSearchError('查詢時發生錯誤。');
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirmInvite = async () => {
    if (searchResult) {
      await store.inviteMember(searchResult.id);
      setIsInviteDialogOpen(false);
      setInviteEmail('');
      setSearchResult(null);
    }
  };

  const handleRoleChange = async (member: GroupMember, newRole: 'admin' | 'member') => {
    if (!currentGroupId) return;
    setRoleTarget(member);
    setIsUpdatingRole(true);
    setRoleError(null);
    try {
      await updateMemberRole(currentGroupId, member.userId, newRole);
    } catch (err: any) {
      let msg = '更新角色失敗';
      try {
        const data = JSON.parse(err.message);
        if (data.message) msg = data.message;
      } catch {
        if (err.message) msg = err.message;
      }
      setRoleError(msg);
    } finally {
      setIsUpdatingRole(false);
      setRoleTarget(null);
    }
  };

  const handleRemoveMember = async () => {
    if (!currentGroupId || !removeTarget) return;
    setIsRemoving(true);
    setRemoveError(null);
    try {
      await removeMember(currentGroupId, removeTarget.userId);
      setRemoveTarget(null);
    } catch (err: any) {
      let msg = '操作失敗';
      try {
        const data = JSON.parse(err.message);
        if (data.message) msg = data.message;
        else if (data.error === 'unsettled_balance') {
          const owes = data.owes ? `應付：$${data.owes.toFixed(0)}` : '';
          const owed = data.owed ? `應收：$${data.owed.toFixed(0)}` : '';
          msg = `帳款尚未結清（${[owes, owed].filter(Boolean).join('、')}），請先完成結算再離開群組。`;
        } else if (data.error === 'last_admin') {
          msg = data.message ?? '請先將管理員轉讓給其他成員，再離開群組';
        }
      } catch {
        if (err.message) msg = err.message;
      }
      setRemoveError(msg);
    } finally {
      setIsRemoving(false);
    }
  };

  const isSelf = (member: GroupMember) => member.userId === currentUser?.id;

  return (
    <div className="space-y-10">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight text-primary mb-2">成員管理</h1>
          <p className="text-xs md:text-base text-muted-foreground font-medium">管理帳本群組中的所有成員及其角色。</p>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="搜尋成員..."
              className="pl-10 bg-muted/50 border-border rounded-xl w-full"
            />
          </div>
          {isCurrentUserAdmin && (
            <Button
              type="button"
              onClick={() => setIsInviteDialogOpen(true)}
              className="rounded-xl h-12 px-6 bg-primary text-primary-foreground font-black flex items-center gap-2 shadow-wash hover:scale-105 transition-all w-full sm:w-auto"
            >
              <UserPlus size={20} />
              邀請新成員
            </Button>
          )}
        </div>
      </div>

      {roleError && (
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center gap-3 text-destructive text-sm font-bold">
          <AlertCircle size={16} />
          {roleError}
          <button onClick={() => setRoleError(null)} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {groupMembers.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground">
          <Users size={48} className="mx-auto mb-4 opacity-20" />
          <p className="font-bold">尚無群組成員資料</p>
          <p className="text-sm mt-1 opacity-60">請先確認已加入群組或邀請成員</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => {
            const self = isSelf(member);
            const isProcessingRole = isUpdatingRole && roleTarget?.userId === member.userId;
            return (
              <Card
                key={member.userId}
                className="bg-muted/30 border-border/50 rounded-2xl md:rounded-[2.5rem] p-5 shadow-sm group hover:border-primary/50 transition-all relative overflow-hidden flex flex-col gap-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="h-14 w-14 rounded-2xl border-2 border-background shadow-lg overflow-hidden bg-muted flex items-center justify-center cursor-pointer"
                    onClick={() => setSelectedMember(member)}
                  >
                    {member.avatarUrl ? (
                      <img src={member.avatarUrl} className="w-full h-full object-cover" alt={member.nickname} />
                    ) : (
                      <span className="text-xl font-black text-primary">{member.nickname?.[0] || '？'}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {member.role === 'admin' ? (
                      <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30 rounded-lg px-2 py-1 text-[10px] font-black flex items-center gap-1">
                        <Crown size={10} />
                        管理員
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="rounded-lg bg-background/50 border-none px-2 py-1 text-[10px] font-bold text-muted-foreground flex items-center gap-1">
                        <Shield size={10} />
                        成員
                      </Badge>
                    )}
                    {self && (
                      <Badge className="bg-primary text-primary-foreground rounded-full px-2 py-0 text-[8px] font-black uppercase">YOU</Badge>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-black text-foreground leading-tight">{member.nickname}</h3>
                  <p className="text-[11px] text-muted-foreground font-medium mt-0.5 truncate">{member.email}</p>
                </div>

                <div className="flex items-center gap-2 mt-auto">
                  {isCurrentUserAdmin && !self && (
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        disabled={isProcessingRole}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl h-9 px-3 text-xs font-bold border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50"
                      >
                        {isProcessingRole ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : member.role === 'admin' ? (
                          <ShieldCheck size={12} />
                        ) : (
                          <Shield size={12} />
                        )}
                        {member.role === 'admin' ? '管理員' : '一般成員'}
                        <ChevronDown size={10} className="opacity-50" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="rounded-xl">
                        <DropdownMenuItem
                          disabled={member.role === 'admin'}
                          onClick={() => handleRoleChange(member, 'admin')}
                          className="gap-2 text-sm font-bold"
                        >
                          <Crown size={14} className="text-amber-500" />
                          設為管理員
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={member.role === 'member'}
                          onClick={() => handleRoleChange(member, 'member')}
                          className="gap-2 text-sm font-bold"
                        >
                          <Shield size={14} />
                          設為一般成員
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {isCurrentUserAdmin && !self && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 rounded-xl text-destructive hover:bg-destructive/10 hover:text-destructive p-0 flex-shrink-0"
                      onClick={() => { setRemoveTarget(member); setRemoveError(null); }}
                    >
                      <UserMinus size={14} />
                    </Button>
                  )}

                  {self && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 h-9 rounded-xl text-destructive hover:bg-destructive/10 text-xs font-bold gap-1"
                      onClick={() => { setRemoveTarget(member); setRemoveError(null); }}
                    >
                      <LogOut size={12} />
                      離開群組
                    </Button>
                  )}
                </div>

                <div className="absolute -right-4 -bottom-4 opacity-5 pointer-events-none group-hover:rotate-12 transition-transform">
                  <Users size={100} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <DialogContent className="bg-muted border-border rounded-2xl md:rounded-[2.5rem] p-6 md:p-8 max-w-md shadow-wash">
          <DialogHeader>
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4 md:mb-6">
              <Mail size={24} className="md:w-8 md:h-8" />
            </div>
            <DialogTitle className="text-2xl md:text-3xl font-black text-primary">邀請進入分帳</DialogTitle>
            <DialogDescription className="text-xs md:text-sm text-muted-foreground font-medium pt-2">
              輸入對方的電子郵件，將其加入您的家庭分帳群組中。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 md:space-y-6 my-4 md:my-6">
            <div className="flex gap-2">
              <Input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="example@mail.com"
                className="bg-background border-border rounded-xl h-12 text-sm"
              />
              <Button onClick={handleSearch} disabled={isSearching} className="rounded-xl h-12 aspect-square p-0">
                {isSearching ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
              </Button>
            </div>
            {searchResult && (
              <div className="p-3 md:p-4 bg-background/50 rounded-2xl border-2 border-primary/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-8 w-8 md:h-10 md:w-10 rounded-xl overflow-hidden">
                    <AvatarImage src={searchResult.avatarUrl} className="object-cover" />
                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-xs">
                      {searchResult.nickname?.[0] || '？'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-bold text-foreground text-xs md:text-sm">{searchResult.nickname}</p>
                    <p className="text-[10px] text-muted-foreground font-medium">{searchResult.email}</p>
                  </div>
                </div>
                <CheckCircle2 size={20} className="text-secondary md:w-6 md:h-6" />
              </div>
            )}
            {searchError && (
              <div className="p-3 md:p-4 bg-destructive/10 rounded-2xl border border-destructive/20 flex items-center gap-3 text-destructive">
                <AlertCircle size={16} className="md:w-5 md:h-5" />
                <p className="text-xs font-bold leading-tight">{searchError}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={handleConfirmInvite}
              disabled={!searchResult}
              className="w-full h-12 md:h-14 rounded-2xl bg-primary text-primary-foreground font-black text-base md:text-lg shadow-sm"
            >
              確認邀請並加入
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove / Leave Confirm Dialog */}
      {removeTarget && (
        <Dialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
          <DialogContent className="bg-muted border-border rounded-2xl p-6 max-w-sm shadow-wash">
            <DialogHeader>
              <div className="w-12 h-12 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center mb-4">
                {isSelf(removeTarget) ? <LogOut size={22} /> : <UserMinus size={22} />}
              </div>
              <DialogTitle className="text-xl font-black text-foreground">
                {isSelf(removeTarget) ? '確認離開群組？' : `移除成員 ${removeTarget.nickname}？`}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground font-medium pt-1">
                {isSelf(removeTarget)
                  ? '離開群組前，所有帳款必須先結清。此操作無法復原。'
                  : `移除後，${removeTarget.nickname} 將無法查看此群組的帳務資料。`}
              </DialogDescription>
            </DialogHeader>
            {removeError && (
              <div className="mt-2 p-3 bg-destructive/10 rounded-xl border border-destructive/20 flex items-start gap-2 text-destructive text-xs font-bold leading-relaxed">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                {removeError}
              </div>
            )}
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setRemoveTarget(null)}>
                取消
              </Button>
              <Button
                className="flex-1 rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 font-black"
                onClick={handleRemoveMember}
                disabled={isRemoving}
              >
                {isRemoving ? <Loader2 size={16} className="animate-spin" /> : isSelf(removeTarget) ? '確認離開' : '確認移除'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Member Details Modal */}
      <Dialog open={!!selectedMember} onOpenChange={(open) => !open && setSelectedMember(null)}>
        <DialogContent className="max-w-md p-0 border-none bg-transparent shadow-none" showCloseButton={false}>
          <div className="p-4 w-full">
            <div className="relative aspect-square w-full rounded-[3.5rem] overflow-hidden shadow-2xl border-8 border-white/5 bg-muted group">
              <img
                src={selectedMember?.avatarUrl || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                alt={selectedMember?.nickname}
              />
              {!selectedMember?.avatarUrl && (
                <div className="absolute inset-0 flex items-center justify-center text-9xl font-black text-muted-foreground/10 uppercase italic">
                  {selectedMember?.nickname?.[0]}
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 p-8 pt-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-3xl font-black text-white tracking-tight">{selectedMember?.nickname}</h3>
                  {selectedMember?.role === 'admin' && <Crown size={20} className="text-amber-400" />}
                </div>
                <p className="text-sm font-bold text-white/60 tracking-widest uppercase">
                  {selectedMember?.role === 'admin' ? '管理員' : '一般成員'} · {selectedMember?.email}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSelectedMember(null)}
              className="absolute top-6 right-6 p-3 bg-white/20 hover:bg-white/40 text-white rounded-full backdrop-blur-xl transition-all shadow-2xl active:scale-90"
            >
              <X size={24} />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
